package com.islamicmedia.shared.data.cache

import com.islamicmedia.shared.data.api.ApiUsage
import com.islamicmedia.shared.data.prompts.PromptTemplates
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/**
 * Tracks all API costs throughout an analysis pipeline.
 * Thread-safe accumulator that builds a complete CostBreakdown.
 */
class CostTracker {
    private val calls = mutableListOf<ApiCallCost>()

    // Anthropic pricing per million tokens (as of 2025)
    companion object {
        // Claude Haiku 3.5 / 4.5
        const val HAIKU_INPUT_PER_MTOK = 0.80
        const val HAIKU_OUTPUT_PER_MTOK = 4.00
        // Claude Sonnet 3.5 / 4
        const val SONNET_INPUT_PER_MTOK = 3.00
        const val SONNET_OUTPUT_PER_MTOK = 15.00
        // Perplexity Sonar (per-request pricing)
        const val PERPLEXITY_PER_REQUEST = 0.005
    }

    fun trackPerplexityCall(phase: String, query: String = "") {
        calls.add(ApiCallCost(
            phase = phase,
            model = "perplexity-sonar",
            inputTokens = 0,
            outputTokens = 0,
            costUsd = PERPLEXITY_PER_REQUEST,
            detail = query.take(80)
        ))
    }

    fun trackAnthropicCall(phase: String, model: String, usage: ApiUsage?, detail: String = "") {
        val inputTokens = usage?.inputTokens ?: 0
        val outputTokens = usage?.outputTokens ?: 0
        val isHaiku = model.contains("haiku", ignoreCase = true)
        val inputRate = if (isHaiku) HAIKU_INPUT_PER_MTOK else SONNET_INPUT_PER_MTOK
        val outputRate = if (isHaiku) HAIKU_OUTPUT_PER_MTOK else SONNET_OUTPUT_PER_MTOK

        val cost = if (usage != null) {
            (inputTokens * inputRate + outputTokens * outputRate) / 1_000_000.0
        } else {
            // Fallback to hardcoded estimates if usage not available
            if (isHaiku) PromptTemplates.HAIKU_COST else PromptTemplates.SONNET_COST
        }

        calls.add(ApiCallCost(
            phase = phase,
            model = if (isHaiku) "claude-haiku" else "claude-sonnet",
            inputTokens = inputTokens,
            outputTokens = outputTokens,
            costUsd = cost,
            detail = detail.take(80)
        ))
    }

    fun buildBreakdown(): CostBreakdown {
        val perplexityCalls = calls.filter { it.model == "perplexity-sonar" }
        val haikuCalls = calls.filter { it.model == "claude-haiku" }
        val sonnetCalls = calls.filter { it.model == "claude-sonnet" }

        return CostBreakdown(
            totalCostUsd = calls.sumOf { it.costUsd },
            perplexity = ModelCosts(
                callCount = perplexityCalls.size,
                inputTokens = perplexityCalls.sumOf { it.inputTokens },
                outputTokens = perplexityCalls.sumOf { it.outputTokens },
                costUsd = perplexityCalls.sumOf { it.costUsd }
            ),
            haiku = ModelCosts(
                callCount = haikuCalls.size,
                inputTokens = haikuCalls.sumOf { it.inputTokens },
                outputTokens = haikuCalls.sumOf { it.outputTokens },
                costUsd = haikuCalls.sumOf { it.costUsd }
            ),
            sonnet = ModelCosts(
                callCount = sonnetCalls.size,
                inputTokens = sonnetCalls.sumOf { it.inputTokens },
                outputTokens = sonnetCalls.sumOf { it.outputTokens },
                costUsd = sonnetCalls.sumOf { it.costUsd }
            ),
            phases = buildPhaseBreakdown(),
            calls = calls.toList()
        )
    }

    private fun buildPhaseBreakdown(): List<PhaseCost> {
        return calls.groupBy { it.phase }.map { (phase, phaseCalls) ->
            PhaseCost(
                phase = phase,
                callCount = phaseCalls.size,
                totalTokens = phaseCalls.sumOf { it.inputTokens + it.outputTokens },
                costUsd = phaseCalls.sumOf { it.costUsd }
            )
        }
    }
}

@Serializable
data class CostBreakdown(
    @SerialName("total_cost_usd") val totalCostUsd: Double = 0.0,
    val perplexity: ModelCosts = ModelCosts(),
    val haiku: ModelCosts = ModelCosts(),
    val sonnet: ModelCosts = ModelCosts(),
    val phases: List<PhaseCost> = emptyList(),
    val calls: List<ApiCallCost> = emptyList()
)

@Serializable
data class ModelCosts(
    @SerialName("call_count") val callCount: Int = 0,
    @SerialName("input_tokens") val inputTokens: Int = 0,
    @SerialName("output_tokens") val outputTokens: Int = 0,
    @SerialName("cost_usd") val costUsd: Double = 0.0
)

@Serializable
data class PhaseCost(
    val phase: String,
    @SerialName("call_count") val callCount: Int = 0,
    @SerialName("total_tokens") val totalTokens: Int = 0,
    @SerialName("cost_usd") val costUsd: Double = 0.0
)

@Serializable
data class ApiCallCost(
    val phase: String,
    val model: String,
    @SerialName("input_tokens") val inputTokens: Int = 0,
    @SerialName("output_tokens") val outputTokens: Int = 0,
    @SerialName("cost_usd") val costUsd: Double = 0.0,
    val detail: String = ""
)
